///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EngineProductDTO, PdbChannelConfigDTO, PdbChannelFrequenciesEnum } from '../../generated/shared-types.model';
import { GtthConfigLevel } from './config-level.enum';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { UserAuthorities } from '../../services/user/user-authorities';
import { Principal } from '../auth/principal.service';
import { GtthPdbService } from '../../services/product/pdb-service';
import { IProductFrequency } from '../../services/product/product-enums/product-frequencies';
import { _cloneDeep } from '../utils';
import { Observable } from 'rxjs';

@Component({
    selector: 'winde-product-radio-config',
    templateUrl: './product-radio-config.component.html',
    styleUrls: ['./product-radio-config.scss'],
    standalone: false
})
export class GtthProductRadioConfigComponent implements OnInit {
    @Input() currentEngineRadio: EngineProductDTO;
    @Input() pairToEngineRadio: EngineProductDTO;
    @Input() configLevel: GtthConfigLevel;
    @Input() customerId = -1;

    @Input() isValid: boolean;
    @Output() isValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    //need when we have pair engine radios
    @Output() isModifiedRadioList: EventEmitter<[number, number[], number[]]> = new EventEmitter<[number, number[], number[]]>();

    public originEngineRadio: EngineProductDTO;

    public bandwidthList: number[];
    public selectedTabIndex = 0;

    public selectedFrequencies: IProductFrequency[];
    public availableFrequencies: IProductFrequency[];

    public needResetButton: boolean;

    public hasAuthority$ = fromPromise(
        this.principal.hasAnyAuthority([
            UserAuthorities.SIKLU_ADMIN,
            UserAuthorities.SIKLU_ACCOUNTANT,
            UserAuthorities.SIKLU_CUSTOMER_SUPPORT,
            UserAuthorities.SIKLU_SALES,
            UserAuthorities.COMPANY_ADMIN,
            UserAuthorities.COMPANY_PLANNER
        ])
    );

    constructor(
        private readonly pdbService: GtthPdbService,
        private readonly principal: Principal
    ) {}

    ngOnInit(): void {
        this.getOriginEngineRadio();
        this.setBandwidthTabs();
    }

    public changeTab(bandwidth: number): void {
        this.selectedTabIndex = this.bandwidthList.indexOf(bandwidth);
        this.setAvailableAndSelectedFrequencies();
    }

    public closeBandwidthTab(bandwidth: number): void {
        const removedTabIndex = this.bandwidthList.indexOf(bandwidth);
        const lastTabIndex = this.currentEngineRadio.pdbConfig.channelConfigs.length - 1;

        if (this.selectedTabIndex === removedTabIndex || this.selectedTabIndex === lastTabIndex) {
            this.selectedTabIndex = 0;
        } else if (this.selectedTabIndex > removedTabIndex) {
            this.selectedTabIndex--;
        }

        this.currentEngineRadio.pdbConfig.channelConfigs.splice(removedTabIndex, 1);
        this.needResetButton = this.shouldDisplayResetButton(this.currentEngineRadio, this.originEngineRadio);
        this.setBandwidthTabs();
    }

    public resetToOriginalValues(): void {
        this.setUpdatedChannelsList(this.availableFrequencies);
        this.currentEngineRadio.pdbConfig.channelConfigs = _cloneDeep(this.originEngineRadio.pdbConfig.channelConfigs);
        this.setAvailableAndSelectedFrequencies();
        this.setBandwidthTabs();
    }

    public setUpdatedChannelsList(activeList: IProductFrequency[]): void {
        const addedFrequencies = activeList.reduce((acc, freq) => {
            if (!this.currentEngineRadio.pdbConfig.channelConfigs[this.selectedTabIndex].frequencies.includes(freq.value)) {
                acc.push(freq.value);
            }
            return acc;
        }, []);

        this.currentEngineRadio.pdbConfig.channelConfigs[this.selectedTabIndex].frequencies = activeList.map((item) => +item.name);
        this.needResetButton = this.shouldDisplayResetButton(this.currentEngineRadio, this.originEngineRadio);

        const deletedFrequencies = this.originEngineRadio.pdbConfig.channelConfigs[this.selectedTabIndex].frequencies.filter((freq) => {
            return !this.currentEngineRadio.pdbConfig.channelConfigs[this.selectedTabIndex].frequencies.includes(freq);
        });

        this.emitValidity(this.currentEngineRadio);
        this.isModifiedRadioList.emit([this.currentEngineRadio.id, deletedFrequencies, addedFrequencies]);
    }

    private getOriginEngineRadio(): void {
        let productObservable: Observable<EngineProductDTO>;

        switch (this.configLevel) {
            case GtthConfigLevel.COMPANY:
                productObservable = this.pdbService.findEngineProduct(this.currentEngineRadio.id);
                break;
            case GtthConfigLevel.PROJECT:
                productObservable = this.pdbService.findEngineProductOnCompanyLevel(this.currentEngineRadio.id, this.customerId);
                break;
        }

        productObservable.subscribe((originEngineRadio) => {
            this.originEngineRadio = originEngineRadio;
            this.setAvailableAndSelectedFrequencies();
        });
    }

    private shouldDisplayResetButton(current: EngineProductDTO, origin: EngineProductDTO): boolean {
        if (!current || !origin) {
            return false;
        }

        const currentConfigs = current.pdbConfig.channelConfigs;
        const originConfigs = origin.pdbConfig.channelConfigs;

        return (
            currentConfigs.length !== originConfigs.length ||
            !currentConfigs.every((config: PdbChannelConfigDTO, index: number) => this.compareChannelConfigs(config, originConfigs[index]))
        );
    }

    private compareChannelConfigs(a: PdbChannelConfigDTO, b: PdbChannelConfigDTO): boolean {
        return (
            a.bandwidth === b.bandwidth &&
            a.frequencies.length === b.frequencies.length &&
            a.frequencies.every((freq: number, index: number) => freq === b.frequencies[index])
        );
    }

    private setAvailableAndSelectedFrequencies(): void {
        this.availableFrequencies = _cloneDeep(this.originEngineRadio).pdbConfig.channelConfigs[this.selectedTabIndex].frequencies.map(
            (freqItem: PdbChannelFrequenciesEnum) => ({
                id: freqItem,
                name: String(freqItem),
                value: freqItem,
                productTypes: []
            })
        );

        this.selectedFrequencies = this.currentEngineRadio.pdbConfig.channelConfigs[this.selectedTabIndex].frequencies.map((frequency) => {
            const foundFrequency = this.availableFrequencies.find((ItemFromAllFrequency) => ItemFromAllFrequency.value === frequency);

            return {
                id: foundFrequency.value,
                name: String(foundFrequency.value),
                value: foundFrequency.value,
                productTypes: []
            };
        });

        this.needResetButton = this.shouldDisplayResetButton(this.currentEngineRadio, this.originEngineRadio);
    }

    private setBandwidthTabs(): void {
        this.bandwidthList = this.currentEngineRadio.pdbConfig.channelConfigs.map((channel) => channel.bandwidth);
    }

    private emitValidity(radio: EngineProductDTO): void {
        const isValid = radio.pdbConfig.channelConfigs.every((channelConf: PdbChannelConfigDTO) => channelConf.frequencies.length > 0);
        this.isValidChange.emit(isValid);
    }
}
